import { ReactNode } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { LoginNetwork } from "../components/auth";
import { createAppTheme } from "../theme";

// Auth screens are fixed branded pages (dark brand panel + cream form panel),
// independent of the app's light/dark toggle. The form panel is forced to the
// light theme so it always reads on the cream panel.
const formTheme = createAppTheme("light");

interface AuthLayoutProps {
  /** Heading rendered at the top of the form panel. */
  title: string;
  /** Muted line under the title. */
  subtitle?: string;
  /**
   * Slot for per-screen banners (success/error alerts). Rendered between the
   * heading block and the form content, inside the forced-light theme.
   */
  alert?: ReactNode;
  /** Width cap for the form column. Defaults to the login form width. */
  maxWidth?: number;
  children: ReactNode;
}

/**
 * Shared split-screen shell for all auth pages: dark brand panel (with the
 * ambient node-network) on the left, cream form panel on the right. The brand
 * panel is hidden below the lg breakpoint and the form panel goes full width.
 */
const AuthLayout = ({
  title,
  subtitle,
  alert,
  maxWidth = 380,
  children,
}: AuthLayoutProps) => {
  return (
    <div className="flex min-h-screen">
      {/* Left brand panel (desktop) */}
      <div className="relative hidden w-[54%] flex-col overflow-hidden bg-[#1A1B12] p-[52px] lg:flex">
        {/* Ambient node-network fills the whole panel and takes the cursor. */}
        <LoginNetwork className="absolute inset-0 z-0" />
        {/* Soft scrim so overlaid type stays crisp over the densest nodes. */}
        <div
          className="pointer-events-none absolute inset-0 z-[1]"
          style={{
            background:
              "radial-gradient(120% 90% at 12% 92%, rgba(26,27,18,0.62), transparent 58%), radial-gradient(90% 60% at 10% 8%, rgba(26,27,18,0.5), transparent 60%)",
          }}
        />

        {/* Content overlays the network; pointer-events pass through so the
            cursor still reaches the canvas behind it. */}
        <div className="pointer-events-none relative z-10 flex flex-1 flex-col">
          <div className="flex items-center gap-3.5">
            <img
              src="/refineai-wordmark.svg"
              alt="RefineAI"
              className="h-[42px] w-auto"
            />
            <span className="rounded-[7px] border border-[#343924] px-2.5 py-1 indent-[0.34em] text-[11px] font-semibold uppercase tracking-[0.34em] text-[#B7BCA7]">
              Answers
            </span>
          </div>

          <div className="mt-auto">
            <h2 className="mb-[18px] font-serif text-[44px] font-normal leading-[1.14] tracking-tight text-[#F4F2E8]">
              Ask your documents
              <br />
              anything.
            </h2>
            <p className="mb-[30px] max-w-[392px] text-[15px] leading-relaxed text-[#A6A290]">
              RefineAI Answers retrieves grounded, cited responses from your
              knowledge base — never a guess without a source.
            </p>
          </div>

          <div className="mt-[34px] font-mono text-[11.5px] text-[rgba(244,242,232,0.38)]">
            answers.refineai.com.au
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex flex-1 flex-col justify-center bg-[#FAF8EC] px-8 py-14 sm:px-16">
        <div className="mx-auto w-full" style={{ maxWidth }}>
          <h2
            className={`${
              subtitle ? "mb-1.5" : "mb-9"
            } font-serif text-[32px] font-normal tracking-tight text-[#1A1B12]`}
          >
            {title}
          </h2>
          {subtitle && (
            <p className="mb-9 text-[13.5px] text-[#6E6B5C]">{subtitle}</p>
          )}

          <ThemeProvider theme={formTheme}>
            {alert}
            {children}
          </ThemeProvider>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
